import { useCachedActions } from '@hooks/useCachedActions';
import type { FetchData } from '@hooks/useDataFetcher';
import { buildCacheKey } from '@shared/util/cache-utils';
import { useCallback, useMemo, useState } from 'react';
import type { Enum } from 'types/core';
import type {
  IMarket,
  MarketResponse,
  MarketsResponse,
} from 'types/prop-types/models/markets';

import type { SnackbarActions } from './useSnackbar';

interface IState {
  loading: boolean;
  allMarkets: IMarket[];
  availableCustomerTypes: Enum[];
}

interface IActions {
  getMarkets: () => Promise<MarketsResponse>;
  getMarket: (isoCode: string) => Promise<IMarket | undefined>;
}

export type UseMarkets = (
  fetchData: FetchData,
  snackbar: SnackbarActions,
) => [IState, IActions];

export const useMarkets: UseMarkets = (makeRequest, snackbar) => {
  const { loading, createAction } = useCachedActions(snackbar);
  const [markets, setMarkets] = useState<IMarket[]>([]);

  const availableCustomerTypes = useMemo(() => {
    const customerTypes: Enum[] = [];

    markets.forEach((market) => {
      market.configurations.forEach((config) => {
        const isListed = customerTypes.find(
          (t) => t.id === config.customerType,
        );

        if (!isListed) {
          customerTypes.push({
            id: config.customerType!,
            name: config.customerTypeName,
          });
        }
      });
    });

    return customerTypes;
  }, [markets]);

  const getMarkets = useCallback(async () => {
    const response = await createAction(
      () => 'getMarkets',
      async () => {
        const res = await makeRequest<MarketsResponse>('/api/markets');
        const data = res?.data?.length ? res.data : [];
        return { ...res, data };
      },
      { data: [] } as MarketsResponse,
    )();

    const data = response?.data ?? [];
    setMarkets(data);
    return { ...response, data };
  }, [createAction, makeRequest]);

  const getMarket = useCallback(
    (isoCode: string) =>
      createAction(
        () => buildCacheKey('getMarket', { isoCode }),
        async () => {
          const response = await makeRequest<MarketResponse>(
            `/api/markets/${isoCode}`,
          );

          if (response.status === 200) {
            return response.data;
          }

          return undefined;
        },
        undefined,
      )(),
    [createAction, makeRequest],
  );

  return [
    { loading, allMarkets: markets, availableCustomerTypes },
    { getMarkets, getMarket },
  ];
};
